import React, { useEffect } from 'react';
import { useTaskStore } from '../stores/taskStore';
import { useProjectStore } from '../stores/projectStore';
import { useReminderStore } from '../stores/reminderStore';
import { websocketService } from '../services/websocket';

/**
 * Provider that subscribes Zustand stores to real-time WebSocket events.
 *
 * Mount this inside the authenticated portion of the app. On mount it
 * subscribes the task, project, and reminder stores to their respective
 * CRUD events via the WebSocketService. On unmount (logout) it cleans up
 * all subscriptions to prevent memory leaks and duplicate handlers.
 *
 * REV-2: Also subscribes to the WebSocket reconnect event. When the WS
 * connection is restored after a disconnect, any events that occurred
 * during the outage are lost. This provider triggers a full refetch of
 * task, project, and reminder data to reconcile state with the server.
 *
 * REV-03: Reminder events are now wired to the reminder store so that
 * real-time reminder changes (created, updated, deleted, completed) by
 * other users or TaskFly AI are immediately reflected in the UI.
 */

// REV-06: HMR disposal — clean up real-time subscriptions when Vite's HMR
// replaces this module, preventing duplicate handlers and memory leaks.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    const taskStore = useTaskStore.getState();
    const projectStore = useProjectStore.getState();
    const reminderStore = useReminderStore.getState();
    taskStore.unsubscribeFromRealTimeUpdates();
    projectStore.unsubscribeFromRealTimeUpdates();
    reminderStore.unsubscribeFromRealTimeUpdates();
  });
}

export const RealtimeSubscriptionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    // Only subscribe if user is authenticated (token present)
    const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
    if (!hasToken) return;

    // Subscribe stores to real-time WebSocket updates
    const taskStore = useTaskStore.getState();
    const projectStore = useProjectStore.getState();
    const reminderStore = useReminderStore.getState();

    taskStore.subscribeToRealTimeUpdates();
    projectStore.subscribeToRealTimeUpdates();
    reminderStore.subscribeToRealTimeUpdates();

    // REV-2: On WS reconnect, refetch data to catch any missed events
    const unsubReconnect = websocketService.onReconnect(() => {
      taskStore.fetchTasks();
      projectStore.fetchProjects();
      reminderStore.fetchReminders();
    });

    // Cleanup on unmount (logout / component tree teardown)
    return () => {
      taskStore.unsubscribeFromRealTimeUpdates();
      projectStore.unsubscribeFromRealTimeUpdates();
      reminderStore.unsubscribeFromRealTimeUpdates();
      unsubReconnect();
    };
  }, []);

  return <>{children}</>;
};
